import JSZip from 'jszip';
import { parseTheme, parseSlideXml } from './pptx-xml-parser.js';
import * as EMFJS from 'rtf.js/dist/EMFJS.bundle.js';
import log from './log.js';
function getBrightness(hexColor) {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b);
}
export async function parsePPTX(pptxFile) {
    try {
        let buffer;
        if (pptxFile instanceof File) {
            buffer = await pptxFile.arrayBuffer();
        }
        else {
            buffer = pptxFile;
        }
        const zip = await JSZip.loadAsync(buffer);
        const presentationXml = await zip.file('ppt/presentation.xml')?.async('string');
        if (!presentationXml) {
            throw new Error('Invalid PPTX: missing presentation.xml');
        }
        const themeXml = await zip.file('ppt/theme/theme1.xml')?.async('string');
        const theme = themeXml ? parseTheme(themeXml) : undefined;
        const slideMatches = presentationXml.match(/<p:sldId[^>]*r:id="rId(\d+)"/g) || [];
        const slideCount = slideMatches.length;
        const sldSzMatch = presentationXml.match(/<p:sldSz[^>]*>/);
        let width = 960;
        let height = 540;
        if (sldSzMatch) {
            const sldSzTag = sldSzMatch[0];
            const cxMatch = sldSzTag.match(/cx="(\d+)"/);
            const cyMatch = sldSzTag.match(/cy="(\d+)"/);
            if (cxMatch && cyMatch) {
                width = parseInt(cxMatch[1]) / 12700;
                height = parseInt(cyMatch[1]) / 12700;
            }
        }
        const slides = [];
        for (let i = 1; i <= slideCount; i++) {
            const slideXml = await zip.file(`ppt/slides/slide${i}.xml`)?.async('string');
            const slideRelsXml = await zip.file(`ppt/slides/_rels/slide${i}.xml.rels`)?.async('string');
            if (!slideXml)
                continue;
            const mediaMap = {};
            let layoutPath = null;
            if (slideRelsXml) {
                const relMatches = Array.from(slideRelsXml.matchAll(/<Relationship[^>]+>/g));
                for (const relMatch of relMatches) {
                    const rel = relMatch[0];
                    const idMatch = rel.match(/Id="([^"]+)"/);
                    const targetMatch = rel.match(/Target="([^"]+)"/);
                    const typeMatch = rel.match(/Type="([^"]+)"/);
                    if (!idMatch || !targetMatch || !typeMatch)
                        continue;
                    const rId = idMatch[1];
                    const target = targetMatch[1];
                    const type = typeMatch[1];
                    if (target.includes('/media/')) {
                        const mediaFilename = target.split('/').pop();
                        if (mediaFilename) {
                            const mediaFile = zip.file(`ppt/media/${mediaFilename}`);
                            if (mediaFile) {
                                if (mediaFilename.match(/\.emf$/i)) {
                                    const arrayBuffer = await mediaFile.async('arraybuffer');
                                    const svgDataUri = await convertEmfToSvgDataUri(arrayBuffer);
                                    mediaMap[rId] = svgDataUri;
                                }
                                else if (mediaFilename.match(/\.wmf$/i)) {
                                    mediaMap[rId] = 'unsupported:' + mediaFilename;
                                }
                                else {
                                    const mediaBlob = await mediaFile.async('blob');
                                    const dataUri = await blobToDataUri(mediaBlob);
                                    mediaMap[rId] = dataUri;
                                }
                            }
                        }
                    }
                    if (type.includes('slideLayout')) {
                        layoutPath = target.replace('../', 'ppt/');
                    }
                }
            }
            let masterShapes = [];
            let layoutShapes = [];
            let layoutBackground;
            let masterBackground;
            let layoutXml = undefined;
            if (layoutPath && theme) {
                layoutXml = await zip.file(layoutPath)?.async('string');
                const layoutRelsXml = await zip.file(layoutPath.replace('.xml', '.xml.rels').replace('slideLayouts/', 'slideLayouts/_rels/'))?.async('string');
                if (layoutXml) {
                    const layoutMediaMap = { ...mediaMap };
                    if (layoutRelsXml) {
                        const relMatches = Array.from(layoutRelsXml.matchAll(/<Relationship[^>]+>/g));
                        for (const relMatch of relMatches) {
                            const rel = relMatch[0];
                            const idMatch = rel.match(/Id="([^"]+)"/);
                            const targetMatch = rel.match(/Target="([^"]+)"/);
                            if (idMatch && targetMatch && targetMatch[1].includes('/media/')) {
                                const mediaFilename = targetMatch[1].split('/').pop();
                                if (mediaFilename) {
                                    const mediaFile = zip.file(`ppt/media/${mediaFilename}`);
                                    if (mediaFile) {
                                        if (mediaFilename.match(/\.emf$/i)) {
                                            const arrayBuffer = await mediaFile.async('arraybuffer');
                                            const svgDataUri = await convertEmfToSvgDataUri(arrayBuffer);
                                            layoutMediaMap[idMatch[1]] = svgDataUri;
                                        }
                                        else if (mediaFilename.match(/\.wmf$/i)) {
                                            layoutMediaMap[idMatch[1]] = 'unsupported:' + mediaFilename;
                                        }
                                        else {
                                            const mediaBlob = await mediaFile.async('blob');
                                            const dataUri = await blobToDataUri(mediaBlob);
                                            layoutMediaMap[idMatch[1]] = dataUri;
                                        }
                                    }
                                }
                            }
                        }
                    }
                    const layoutResult = parseSlideXml(layoutXml, theme, layoutMediaMap, true);
                    layoutShapes = layoutResult.shapes;
                    layoutBackground = layoutResult.background;
                    if (layoutRelsXml) {
                        const masterMatch = layoutRelsXml.match(/Type="[^"]*slideMaster"[^>]*Target="([^"]+)"/);
                        if (masterMatch) {
                            const masterPath = masterMatch[1].replace('../', 'ppt/');
                            const masterXml = await zip.file(masterPath)?.async('string');
                            const masterRelsXml = await zip.file(masterPath.replace('.xml', '.xml.rels').replace('slideMasters/', 'slideMasters/_rels/'))?.async('string');
                            const masterMediaMap = { ...mediaMap };
                            if (masterRelsXml) {
                                const relMatches = Array.from(masterRelsXml.matchAll(/<Relationship[^>]+>/g));
                                for (const relMatch of relMatches) {
                                    const rel = relMatch[0];
                                    const idMatch = rel.match(/Id="([^"]+)"/);
                                    const targetMatch = rel.match(/Target="([^"]+)"/);
                                    if (idMatch && targetMatch && targetMatch[1].includes('/media/')) {
                                        const mediaFilename = targetMatch[1].split('/').pop();
                                        if (mediaFilename) {
                                            const mediaFile = zip.file(`ppt/media/${mediaFilename}`);
                                            if (mediaFile) {
                                                if (mediaFilename.match(/\.emf$/i)) {
                                                    const arrayBuffer = await mediaFile.async('arraybuffer');
                                                    const svgDataUri = await convertEmfToSvgDataUri(arrayBuffer);
                                                    masterMediaMap[idMatch[1]] = svgDataUri;
                                                }
                                                else if (mediaFilename.match(/\.wmf$/i)) {
                                                    masterMediaMap[idMatch[1]] = 'unsupported:' + mediaFilename;
                                                }
                                                else {
                                                    const mediaBlob = await mediaFile.async('blob');
                                                    const dataUri = await blobToDataUri(mediaBlob);
                                                    masterMediaMap[idMatch[1]] = dataUri;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            if (masterXml) {
                                const masterResult = parseSlideXml(masterXml, theme, masterMediaMap, true);
                                masterShapes = masterResult.shapes;
                                masterBackground = masterResult.background;
                            }
                        }
                    }
                }
            }
            const parseResult = theme
                ? parseSlideXml(slideXml, theme, mediaMap, false, layoutXml, i)
                : { shapes: [], background: undefined };
            const layoutShapesByKey = new Map();
            layoutShapes.forEach((shape) => {
                if (shape.placeholderType !== undefined || shape.placeholderIdx !== undefined) {
                    const key = `${shape.placeholderType ?? 'null'}:${shape.placeholderIdx ?? 'default'}`;
                    layoutShapesByKey.set(key, shape);
                }
            });
            parseResult.shapes.forEach((shape) => {
                if (shape.placeholderType !== undefined || shape.placeholderIdx !== undefined) {
                    const key = `${shape.placeholderType ?? 'null'}:${shape.placeholderIdx ?? 'default'}`;
                    const layoutShape = layoutShapesByKey.get(key);
                    if (layoutShape) {
                        const hasDefaultPosition = (shape.position.x === 36 && shape.position.y === 28) ||
                            (shape.position.x === 0 && shape.position.y === 0);
                        const hasZeroSize = shape.size.width === 0 || shape.size.height === 0;
                        if (hasDefaultPosition || hasZeroSize) {
                            if (layoutShape.position.x !== 0 || layoutShape.position.y !== 0) {
                                shape.position.x = layoutShape.position.x;
                                shape.position.y = layoutShape.position.y;
                            }
                            if (layoutShape.size.width !== 0 || layoutShape.size.height !== 0) {
                                shape.size.width = layoutShape.size.width;
                                shape.size.height = layoutShape.size.height;
                            }
                        }
                        if (shape.textStyle && layoutShape.textStyle) {
                            if (!shape.textStyle.align && layoutShape.textStyle.align) {
                                shape.textStyle.align = layoutShape.textStyle.align;
                            }
                            if (!shape.textStyle.fontSize && layoutShape.textStyle.fontSize) {
                                shape.textStyle.fontSize = layoutShape.textStyle.fontSize;
                            }
                            if (!shape.textStyle.verticalAnchor && layoutShape.textStyle.verticalAnchor) {
                                shape.textStyle.verticalAnchor = layoutShape.textStyle.verticalAnchor;
                            }
                        }
                    }
                }
                if (shape.textStyle && !shape.textStyle.align) {
                    shape.textStyle.align = 'left';
                }
            });
            const slideHasContent = new Set();
            const slideHasTextContent = new Set();
            const slideTextValues = new Set();
            parseResult.shapes.forEach((shape) => {
                if (shape.placeholderType !== undefined || shape.placeholderIdx !== undefined) {
                    const key = `${shape.placeholderType ?? 'null'}:${shape.placeholderIdx ?? 'default'}`;
                    slideHasContent.add(key);
                    let shapeText = shape.text || '';
                    if (!shapeText && shape.paragraphs && shape.paragraphs.length > 0) {
                        shapeText = shape.paragraphs.map(p => p.runs.map(r => r.text).join('')).join('\n');
                    }
                    const trimmedShapeText = shapeText.trim();
                    if (shapeText && shapeText.length > 0 &&
                        !shapeText.includes('Click to edit') &&
                        !shapeText.includes('Click to add')) {
                        slideHasTextContent.add(key);
                        slideTextValues.add(trimmedShapeText);
                    }
                }
            });
            const slideHasAnyContent = parseResult.shapes.some(s => {
                const hasText = (s.text && s.text.length > 0) ||
                    (s.paragraphs && s.paragraphs.length > 0 && s.paragraphs.some(p => p.runs && p.runs.length > 0));
                return !s.placeholderType || (s.placeholderType && hasText);
            });
            const slideHasTitleLike = parseResult.shapes.some(s => {
                const hasText = (s.text && s.text.length > 0) ||
                    (s.paragraphs && s.paragraphs.length > 0 && s.paragraphs.some(p => p.runs && p.runs.length > 0));
                return (s.placeholderType === 'title' || s.placeholderType === 'ctrTitle' || s.placeholderType === 'subTitle') &&
                    hasText;
            });
            const slideHasBodyLike = parseResult.shapes.some(s => {
                const hasText = (s.text && s.text.length > 0) ||
                    (s.paragraphs && s.paragraphs.length > 0 && s.paragraphs.some(p => p.runs && p.runs.length > 0));
                return (s.placeholderType === 'body' || s.placeholderType === 'obj' || s.placeholderType === 'subTitle') &&
                    hasText;
            });
            const layoutHasContent = new Set();
            const layoutHasTitleLike = layoutShapes.some(s => s.placeholderType === 'title' || s.placeholderType === 'ctrTitle' || s.placeholderType === 'subTitle');
            const layoutHasBodyLike = layoutShapes.some(s => s.placeholderType === 'body' || s.placeholderType === 'obj' || s.placeholderType === 'subTitle');
            layoutShapes.forEach((shape) => {
                if (shape.placeholderType !== undefined || shape.placeholderIdx !== undefined) {
                    const key = `${shape.placeholderType ?? 'null'}:${shape.placeholderIdx ?? 'default'}`;
                    layoutHasContent.add(key);
                }
            });
            const filteredMasterShapes = masterShapes.filter((shape) => {
                if (shape.placeholderType === undefined && shape.placeholderIdx === undefined) {
                    return true;
                }
                const shapeHasText = shape.text && shape.text.length > 0;
                if (shape.placeholderType === null && shape.placeholderIdx === undefined && !shapeHasText) {
                    return true;
                }
                if (shape.placeholderType === 'title' && layoutHasTitleLike) {
                    return false;
                }
                if (shape.placeholderType === 'body' && layoutHasBodyLike) {
                    return false;
                }
                const trimmedText = shape.text?.trim() || '';
                const isEmptyOrPlaceholder = !shape.text || shape.text.length === 0 ||
                    shape.text.includes('Click to edit') ||
                    shape.text.includes('Click to add') ||
                    trimmedText === 'Insert Text';
                if (slideHasAnyContent && isEmptyOrPlaceholder) {
                    return false;
                }
                const key = `${shape.placeholderType ?? 'null'}:${shape.placeholderIdx ?? 'default'}`;
                return !layoutHasContent.has(key);
            });
            const filteredLayoutShapes = layoutShapes.filter((shape) => {
                let layoutText = shape.text || '';
                if (!layoutText && shape.paragraphs && shape.paragraphs.length > 0) {
                    layoutText = shape.paragraphs.map(p => p.runs.map(r => r.text).join('')).join('\n');
                }
                if (layoutText && slideTextValues.has(layoutText.trim())) {
                    return false;
                }
                if (shape.placeholderType === undefined && shape.placeholderIdx === undefined) {
                    return true;
                }
                const shapeHasText = shape.text && shape.text.length > 0;
                if (shape.placeholderType === null && shape.placeholderIdx === undefined && !shapeHasText) {
                    return true;
                }
                const trimmedText = shape.text?.trim() || '';
                const isEmptyOrPlaceholder = !shape.text || shape.text.length === 0 ||
                    shape.text.includes('Click to edit') ||
                    shape.text.includes('Click to add') ||
                    trimmedText === 'Insert Text';
                if (slideHasAnyContent && isEmptyOrPlaceholder) {
                    return false;
                }
                const key = `${shape.placeholderType ?? 'null'}:${shape.placeholderIdx ?? 'default'}`;
                if (slideHasTextContent.has(key)) {
                    return false;
                }
                return !slideHasContent.has(key);
            });
            if (typeof process !== 'undefined' && process.env?.DEBUG_SHAPES) {
                log.info(`[Shape Merging] Slide ${i + 1}`, {
                    masterShapes: masterShapes.length,
                    layoutShapes: layoutShapes.length,
                    slideShapes: parseResult.shapes.length,
                    filteredMaster: filteredMasterShapes.length,
                    filteredLayout: filteredLayoutShapes.length,
                    slideHasTitleLike,
                    slideHasBodyLike,
                    slideHasContent: Array.from(slideHasContent),
                    slideHasTextContent: Array.from(slideHasTextContent),
                });
                filteredLayoutShapes.forEach(s => {
                    const key = `${s.placeholderType ?? 'null'}:${s.placeholderIdx ?? 'default'}`;
                    log.info(`[Layout Shape Kept] Slide ${i + 1}`, {
                        key,
                        hasText: !!s.text,
                        textLength: s.text?.length || 0,
                        slideHasThis: slideHasContent.has(key),
                        slideHasTextFor: slideHasTextContent.has(key),
                    });
                });
            }
            let allShapes = [...filteredMasterShapes, ...filteredLayoutShapes, ...parseResult.shapes];
            if (typeof process !== 'undefined' && process.env?.DEBUG_SHAPES) {
                log.info(`[Shapes Detail] Slide ${i + 1}`, {
                    totalShapes: allShapes.length,
                    shapes: allShapes.map(s => ({
                        type: s.type,
                        placeholderType: s.placeholderType,
                        placeholderIdx: s.placeholderIdx,
                        hasText: s.text ? s.text.substring(0, 50) : null,
                        hasParagraphs: s.paragraphs ? s.paragraphs.length : 0,
                        position: `${s.position.x},${s.position.y}`,
                        size: `${s.size.width}x${s.size.height}`,
                    }))
                });
            }
            allShapes.forEach(shape => {
                if (shape.isBackgroundImage) {
                }
                else if (shape.type === 'image' &&
                    shape.position.x <= 60 && shape.position.y <= 60 &&
                    shape.size.width >= (width * 0.85) && shape.size.height >= (height * 0.75)) {
                    shape.isBackgroundImage = true;
                }
            });
            allShapes = allShapes.sort((a, b) => {
                const aIsBackground = a.isBackgroundImage;
                const bIsBackground = b.isBackgroundImage;
                if (aIsBackground && !bIsBackground)
                    return -1;
                if (!aIsBackground && bIsBackground)
                    return 1;
                return 0;
            });
            const finalBackground = parseResult.background || layoutBackground || masterBackground;
            if (finalBackground && theme) {
                const hasBackgroundImage = allShapes.some(shape => shape.isBackgroundImage);
                if (hasBackgroundImage) {
                    allShapes.forEach((shape) => {
                        if (shape.type === 'text' && shape.textStyle?.color) {
                            const textColor = shape.textStyle.color;
                            if (typeof textColor !== 'string')
                                return;
                            const brightness = getBrightness(textColor);
                            if (brightness > 128) {
                                return;
                            }
                            shape.textStyle.color = '#FFFFFF';
                            if (shape.paragraphs) {
                                shape.paragraphs.forEach(p => {
                                    p.runs.forEach(r => {
                                        if (r.color && typeof r.color === 'string' && getBrightness(r.color) <= 128) {
                                            r.color = '#FFFFFF';
                                        }
                                        else if (!r.color) {
                                            r.color = '#FFFFFF';
                                        }
                                    });
                                });
                            }
                        }
                    });
                }
                else {
                    allShapes.forEach((shape) => {
                        if (shape.type === 'text' && shape.textStyle?.color) {
                            if (shape.fill) {
                                return;
                            }
                            if (!shape.placeholderType && !shape.placeholderIdx) {
                                return;
                            }
                            let textHex;
                            if (typeof shape.textStyle.color === 'string') {
                                textHex = shape.textStyle.color.startsWith('#') ? shape.textStyle.color : '#' + shape.textStyle.color;
                            }
                            else {
                                const c = shape.textStyle.color;
                                textHex = '#' + ((1 << 24) + (c.r << 16) + (c.g << 8) + c.b).toString(16).slice(1);
                            }
                            if (typeof finalBackground !== 'string') {
                                return;
                            }
                            const bgHex = finalBackground.startsWith('#') ? finalBackground : '#' + finalBackground;
                            const bgBrightness = getBrightness(bgHex);
                            const textBrightness = getBrightness(textHex);
                            const insufficientContrast = ((bgBrightness > 200 && textBrightness > 200) ||
                                (bgBrightness < 55 && textBrightness < 55));
                            if (insufficientContrast) {
                                if (bgBrightness < 128) {
                                    shape.textStyle.color = theme.colors.lt1.startsWith('#') ? theme.colors.lt1 : '#' + theme.colors.lt1;
                                }
                                else {
                                    if (shape.placeholderType === 'title' || shape.placeholderType === 'ctrTitle') {
                                        shape.textStyle.color = theme.colors.accent1.startsWith('#') ? theme.colors.accent1 : '#' + theme.colors.accent1;
                                    }
                                    else {
                                        shape.textStyle.color = theme.colors.dk1.startsWith('#') ? theme.colors.dk1 : '#' + theme.colors.dk1;
                                    }
                                }
                            }
                        }
                    });
                }
            }
            if (theme) {
                allShapes.forEach((shape) => {
                    if (shape.type === 'text' && shape.textStyle?.color && (shape.placeholderType === 'title' || shape.placeholderType === 'ctrTitle' || shape.placeholderType === 'body')) {
                        const currentColor = typeof shape.textStyle.color === 'string' ? shape.textStyle.color.toUpperCase() : null;
                        const accent1Upper = theme.colors.accent1.toUpperCase();
                        const dk1Upper = theme.colors.dk1.toUpperCase();
                        if (currentColor && (shape.placeholderType === 'title' || shape.placeholderType === 'ctrTitle')) {
                            const colorWithoutHash = currentColor.replace('#', '');
                            const accent1WithoutHash = accent1Upper.replace('#', '');
                            if (colorWithoutHash === accent1WithoutHash || colorWithoutHash === '4285F4') {
                                shape.textStyle.color = '#1E1069';
                            }
                        }
                        if (currentColor && shape.placeholderType === 'body') {
                            const colorWithoutHash = currentColor.replace('#', '');
                            const dk1WithoutHash = dk1Upper.replace('#', '');
                            if (colorWithoutHash === dk1WithoutHash || colorWithoutHash === '000000') {
                                shape.textStyle.color = '#666666';
                            }
                        }
                    }
                });
            }
            let speakerNotes;
            const notesSlideXml = await zip.file(`ppt/notesSlides/notesSlide${i}.xml`)?.async('string');
            if (notesSlideXml) {
                const textMatches = notesSlideXml.matchAll(/<a:t>([^<]*)<\/a:t>/g);
                const textFragments = [];
                for (const match of textMatches) {
                    const text = match[1].trim();
                    if (text) {
                        textFragments.push(text);
                    }
                }
                if (textFragments.length > 0) {
                    speakerNotes = textFragments.join(' ')
                        .replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>')
                        .replace(/&quot;/g, '"')
                        .replace(/&apos;/g, "'");
                }
            }
            slides.push({
                number: i,
                background: finalBackground,
                shapes: allShapes,
                masterShapes,
                layoutShapes,
                slideShapes: parseResult.shapes,
                notes: speakerNotes,
            });
        }
        return {
            slides,
            size: { width, height },
            theme,
        };
    }
    catch (error) {
        throw new Error(`Failed to parse PPTX: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}
function createMinimalDOMShim() {
    const elements = new Map();
    const doc = {
        createElementNS: (ns, tagName) => {
            const el = { _ns: ns, _tag: tagName };
            elements.set(el, { tagName, attributes: new Map(), children: [] });
            return el;
        },
        createTextNode: (text) => {
            const node = { _text: text };
            elements.set(node, { tagName: '#text', attributes: new Map(), children: [], textContent: text });
            return node;
        }
    };
    const elementProto = {
        setAttribute: function (name, value) {
            const data = elements.get(this);
            if (data)
                data.attributes.set(name, value);
        },
        setAttributeNS: function (_ns, name, value) {
            const data = elements.get(this);
            if (data)
                data.attributes.set(name, value);
        },
        appendChild: function (child) {
            const data = elements.get(this);
            if (data)
                data.children.push(child);
            return child;
        },
        getAttribute: function (name) {
            const data = elements.get(this);
            return data?.attributes.get(name);
        }
    };
    const originalCreateElementNS = doc.createElementNS;
    doc.createElementNS = (ns, tagName) => {
        const el = originalCreateElementNS(ns, tagName);
        Object.assign(el, elementProto);
        return el;
    };
    const serializer = {
        serializeToString: (el) => {
            const data = elements.get(el);
            if (!data)
                return '';
            if (data.tagName === '#text')
                return data.textContent || '';
            const attrs = Array.from(data.attributes.entries())
                .map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`)
                .join(' ');
            const children = data.children.map((child) => serializer.serializeToString(child)).join('');
            return `<${data.tagName}${attrs ? ' ' + attrs : ''}>${children}</${data.tagName}>`;
        }
    };
    return { document: doc, XMLSerializer: serializer };
}
async function convertEmfToSvgDataUri(arrayBuffer) {
    try {
        if (EMFJS && typeof EMFJS.loggingEnabled === 'function') {
            EMFJS.loggingEnabled(false);
        }
        if (typeof document === 'undefined') {
            const { document: doc, XMLSerializer: Serializer } = createMinimalDOMShim();
            global.document = doc;
            global.XMLSerializer = class {
                constructor() {
                    this.serializeToString = Serializer.serializeToString;
                }
            };
            try {
                const EMFJSModule = EMFJS.default || EMFJS;
                const renderer = new EMFJSModule.Renderer(arrayBuffer);
                const svg = renderer.render({
                    width: '100%',
                    height: '100%',
                    wExt: 1000,
                    hExt: 1000,
                    xExt: 1000,
                    yExt: 1000,
                    mapMode: 8
                });
                const svgString = Serializer.serializeToString(svg);
                const encodedSvg = Buffer.from(svgString).toString('base64');
                return `data:image/svg+xml;base64,${encodedSvg}`;
            }
            finally {
                delete global.document;
                delete global.XMLSerializer;
            }
        }
        const EMFJSModule = EMFJS.default || EMFJS;
        const renderer = new EMFJSModule.Renderer(arrayBuffer);
        const svg = renderer.render({
            width: '100%',
            height: '100%',
            wExt: 1000,
            hExt: 1000,
            xExt: 1000,
            yExt: 1000,
            mapMode: 8
        });
        const svgString = new XMLSerializer().serializeToString(svg);
        const encodedSvg = btoa(unescape(encodeURIComponent(svgString)));
        return `data:image/svg+xml;base64,${encodedSvg}`;
    }
    catch (error) {
        console.error('Failed to convert EMF to SVG:', error);
        console.error('EMF buffer size:', arrayBuffer.byteLength, 'bytes');
        return 'unsupported:emf-conversion-failed';
    }
}
async function blobToDataUri(blob) {
    if (typeof FileReader === 'undefined') {
        const arrayBuffer = await blob.arrayBuffer();
        const buffer = Buffer.from(arrayBuffer);
        const mimeType = blob.type || 'application/octet-stream';
        return `data:${mimeType};base64,${buffer.toString('base64')}`;
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
//# sourceMappingURL=parser.js.map